import { type EnumListDef, type EnumValueDef, matchEnumValue } from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Archive, ArrowDown, ArrowUp, ListChecks, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { ColorSlotPicker, Swatch, slotName } from '../components/schema/color-slot-picker';
import { CheckRow, EditorError, LabeledInput, Panel, splitList, TextRow } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { useSchema, useSchemaEdit } from '../lib/schema';

export const Route = createFileRoute('/admin/lists')({
  validateSearch: (s: Record<string, unknown>): { list?: string } => (typeof s.list === 'string' ? { list: s.list } : {}),
  component: ListsPage,
});

function ListsPage() {
  const { data: snap } = useSchema();
  const { list: listKey } = Route.useSearch();
  const [adding, setAdding] = useState(false);
  if (!snap) return <PageHeader title="Value lists" description="Loading…" />;
  const list = snap.lists.find(l => l.key === (listKey ?? 'camp')) ?? snap.lists[0];
  return (
    <>
      <PageHeader
        title="Value lists"
        description="Camp, Status, Investigation status, rating scale and dimensions, and every other choice list."
      />
      <Tip id="admin-lists" learnMore="schema-and-templates">
        Imported values match a list value by its label or any synonym, whatever the case. Extra words are kept as a note (“ACTIVE (reduced
        activity since 2018)” → Active + note).
      </Tip>
      <div className="mt-4 grid gap-6 lg:grid-cols-[15rem_1fr]">
        <nav aria-label="Value lists" className="space-y-3">
          <ul className="space-y-0.5">
            {snap.lists.map(l => (
              <li key={l.id}>
                <Link
                  to="/admin/lists"
                  search={{ list: l.key }}
                  aria-current={l.id === list?.id ? 'page' : undefined}
                  className="flex items-center justify-between rounded-md px-2 py-1 text-sm hover:bg-muted aria-[current=page]:bg-muted aria-[current=page]:font-medium"
                >
                  {l.label}
                  <span className="text-xs text-muted-foreground tabular">{l.values.filter(v => !v.archived).length}</span>
                </Link>
              </li>
            ))}
          </ul>
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" /> New list
          </Button>
        </nav>
        <div className="min-w-0 space-y-6">
          {adding && <NewList onDone={() => setAdding(false)} />}
          {list ? (
            <ListEditor key={list.id} list={list} />
          ) : (
            <EmptyState icon={ListChecks} title="No lists yet" learnMore="schema-and-templates" />
          )}
        </div>
      </div>
    </>
  );
}

function NewList({ onDone }: { onDone: () => void }) {
  const edit = useSchemaEdit();
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  return (
    <Panel title="New list">
      <form
        className="space-y-3"
        onSubmit={e => {
          e.preventDefault();
          edit.mutate({ path: '/admin/schema/lists', body: { label, description } }, { onSuccess: onDone });
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <TextRow label="Name" required value={label} onChange={setLabel} placeholder="e.g. Media format" />
          <TextRow label="Description" value={description} onChange={setDescription} />
        </div>
        <EditorError error={edit.error} />
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={!label.trim() || edit.isPending}>
            Create list
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function ListEditor({ list }: { list: EnumListDef }) {
  const edit = useSchemaEdit();
  const [editing, setEditing] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [trial, setTrial] = useState('');
  const hasScores = list.values.some(v => v.score !== null) || list.key.includes('scale');
  const values = list.values.filter(v => showArchived || !v.archived);
  const match = trial.trim() ? matchEnumValue(list.values, trial) : null;
  const move = (i: number, d: number) => {
    const ids = list.values.map(v => v.id);
    const [id] = ids.splice(i, 1);
    if (id) ids.splice(i + d, 0, id);
    edit.mutate({ path: `/admin/schema/lists/${list.id}/order`, body: { ids } });
  };
  return (
    <>
      <Panel
        title={
          <span>
            {list.label} <code className="text-xs font-normal text-muted-foreground">{list.key}</code>
          </span>
        }
      >
        {list.description && <p className="text-sm text-muted-foreground">{list.description}</p>}
        <ul className="divide-y rounded-md border" aria-label={`Values of ${list.label}`}>
          {values.map(v => {
            const i = list.values.indexOf(v);
            return (
              <li key={v.id} className="p-2" aria-label={v.label}>
                {editing === v.id ? (
                  <ValueForm list={list} value={v} hasScores={hasScores} onDone={() => setEditing(null)} />
                ) : (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Swatch slot={v.colorSlot} />
                    <span className="sr-only">{slotName(v.colorSlot)}</span>
                    <span className={`font-medium ${v.archived ? 'text-muted-foreground line-through' : ''}`}>{v.label}</span>
                    {v.score !== null && <span className="rounded bg-muted px-1.5 text-xs tabular">{v.score}/5</span>}
                    {v.synonyms.length > 0 && <span className="text-xs text-muted-foreground">also: {v.synonyms.join(', ')}</span>}
                    <span className="ms-auto flex gap-1">
                      <Button size="icon" variant="ghost" aria-label={`Move ${v.label} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                        <ArrowUp aria-hidden="true" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Move ${v.label} down`}
                        disabled={i === list.values.length - 1}
                        onClick={() => move(i, 1)}
                      >
                        <ArrowDown aria-hidden="true" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(v.id)}>
                        <Pencil aria-hidden="true" /> Edit<span className="sr-only"> {v.label}</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          edit.mutate({ path: `/admin/schema/values/${v.id}`, method: 'PATCH', body: { archived: !v.archived } })
                        }
                      >
                        <Archive aria-hidden="true" /> {v.archived ? 'Restore' : 'Archive'}
                        <span className="sr-only"> {v.label}</span>
                      </Button>
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <CheckRow label="Show archived values" checked={showArchived} onChange={setShowArchived} />
        <EditorError error={edit.error} />
        {editing === 'new' ? (
          <ValueForm list={list} hasScores={hasScores} onDone={() => setEditing(null)} />
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
            <Plus aria-hidden="true" /> Add value
          </Button>
        )}
      </Panel>
      <Panel title="Try the matching">
        <LabeledInput
          label="Text as written in a sheet"
          value={trial}
          onChange={e => setTrial(e.target.value)}
          placeholder="e.g. ACTIVE (reduced activity since 2018)"
        />
        {match && (
          <p role="status" className="text-sm" data-testid="match-result">
            {match.value ? (
              <>
                → <strong>{match.value.label}</strong>
                {match.how === 'synonym' && ' (synonym)'}
                {match.note && (
                  <>
                    {' '}
                    + note “<bdi dir="auto">{match.note}</bdi>”
                  </>
                )}
              </>
            ) : (
              <>No match: a reviewer picks a value and the text is kept as a note, or add it to the list.</>
            )}
          </p>
        )}
      </Panel>
    </>
  );
}

function ValueForm({
  list,
  value,
  hasScores,
  onDone,
}: {
  list: EnumListDef;
  value?: EnumValueDef;
  hasScores: boolean;
  onDone: () => void;
}) {
  const edit = useSchemaEdit();
  const [label, setLabel] = useState(value?.label ?? '');
  const [synonyms, setSynonyms] = useState(value?.synonyms.join(', ') ?? '');
  const [slot, setSlot] = useState<number | null>(value?.colorSlot ?? null);
  const [score, setScore] = useState(value?.score?.toString() ?? '');
  const [description, setDescription] = useState(value?.description ?? '');
  return (
    <form
      className="space-y-3 rounded-md bg-background p-2"
      aria-label={value ? `Edit ${value.label}` : `New value in ${list.label}`}
      onSubmit={e => {
        e.preventDefault();
        const body = { label, synonyms: splitList(synonyms), colorSlot: slot, score: score === '' ? null : Number(score), description };
        edit.mutate(
          value
            ? { path: `/admin/schema/values/${value.id}`, method: 'PATCH', body }
            : { path: `/admin/schema/lists/${list.id}/values`, body },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <TextRow label="Label" required value={label} onChange={setLabel} />
        <TextRow
          label="Synonyms"
          value={synonyms}
          onChange={setSynonyms}
          placeholder="e.g. formerly, former"
          help="Other spellings that mean this value, separated by commas. Matching ignores case and accents."
        />
        {hasScores && <TextRow label="Score (1–5)" value={score} onChange={setScore} help="The number this rating word is stored as." />}
        <TextRow label="Description" value={description} onChange={setDescription} />
      </div>
      <ColorSlotPicker label="Color (for color rules)" value={slot} onChange={setSlot} />
      <EditorError error={edit.error} />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!label.trim() || edit.isPending}>
          {value ? 'Save value' : 'Add value'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
